import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  AccountList,
  AccountTestResult,
  AutomationActivityList,
  AutomationTokenCreateResponse,
  AutomationTokenList,
  AutomationTokenRevokeResponse,
  AuthState,
  DisplayPreferencesResponse,
  FolderList,
  InviteCheckResponse,
  InviteCreateResponse,
  InviteList,
  LoginResponse,
  MailNavigationCounts,
  OperationAcknowledgeResponse,
  OperationRetryResponse,
  SyncReconcileResponse,
  SyncRefreshResponse,
  SyncStatusResponse,
  NotificationState,
  StorageCapacityResponse,
  TotpConfirmResponse,
  TotpEnrollResponse,
  UserList,
  VersionInfo,
  type Account,
  type AccountCreate,
  type AccountTest,
  type AccountUpdate,
  type AutomationTokenCreate,
  type DisplayPreferences,
  type UserPatch,
} from '@email/shared';
import { api } from './client.js';
import {
  setWintermuteBadge,
  unsubscribeCurrentPushDevice,
} from '../pwa/notifications.js';
import {
  appInvalidations,
  appKeys,
  mailInvalidations,
  mailKeys,
  settingsInvalidations,
  settingsKeys,
} from './query-cache.js';

// ---- auth ----

export function useAuthState() {
  return useQuery({
    queryKey: appKeys.auth,
    queryFn: () => api('/api/auth/me', { schema: AuthState }),
    staleTime: 60_000,
    retry: false,
  });
}

export function useDisplayPreferences(userId: number | null) {
  return useQuery({
    queryKey: settingsKeys.preference(userId ?? 0),
    queryFn: () => api('/api/preferences', { schema: DisplayPreferencesResponse }),
    enabled: userId !== null,
  });
}

export function saveDisplayPreferences(preferences: DisplayPreferences) {
  return api('/api/preferences', {
    method: 'PATCH',
    body: preferences,
    schema: DisplayPreferencesResponse,
  });
}

/**
 * Reset all per-user browser state when the signed-in identity changes.
 * The query cache is cleared FIRST: keys carry no user id and bodies are
 * cached with staleTime: Infinity, so a stale cache would serve the previous
 * user's mail — and a failed lazy vault import must not skip the clear.
 * The offline vault is locked (not deleted): the previous owner's data stays
 * theirs, just sealed behind their passphrase again.
 */
async function resetIdentityBoundState(qc: QueryClient) {
  qc.clear();
  void appInvalidations.auth.invalidate(qc);
  const offline = await import('../offline/vault.js').catch(() => null);
  offline?.lockOfflineVault();
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      api('/api/auth/login', { method: 'POST', body, schema: LoginResponse }),
    // A new identity may replace a previous user without an explicit logout
    // (expired session, direct /login navigation). Reset everything bound to
    // the previous identity; see resetIdentityBoundState.
    onSuccess: () => resetIdentityBoundState(qc),
  });
}

export function useTotpVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api('/api/auth/totp', { method: 'POST', body: { code } }),
    onSuccess: () => resetIdentityBoundState(qc),
  });
}

export function useSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      api('/api/auth/setup', { method: 'POST', body }),
    onSuccess: () => resetIdentityBoundState(qc),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Invalidate the browser-side encryption keys before ending the server
      // session so an already queued alert cannot reveal the prior user's
      // content after somebody else signs into this browser.
      await unsubscribeCurrentPushDevice().catch(() => undefined);
      await setWintermuteBadge(null);
      const offline = await import('../offline/vault.js');
      offline.lockOfflineVault();
      await offline.deleteOfflineVault().catch(() => undefined);
      return api('/api/auth/logout', { method: 'POST' });
    },
    onSuccess: () => {
      qc.clear();
      void appInvalidations.auth.invalidate(qc);
    },
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { current: string; next: string }) =>
      api('/api/auth/password', { method: 'POST', body }),
    onSuccess: async () => {
      // The server rotates the session and deletes every session-bound push
      // subscription — including this device's. Re-register the browser's
      // existing subscription against the fresh session so notifications
      // keep working without a manual disable/re-enable round-trip.
      try {
        const notifications = await import('../pwa/notifications.js');
        const state = await notifications.currentPushBrowserState();
        if (state.permission === 'granted' && state.endpointHash) {
          const { publicKey } = await api('/api/notifications', {
            schema: NotificationState,
          });
          await notifications.enablePushOnThisDevice(
            publicKey,
            notifications.suggestedDeviceName(),
          );
        }
      } catch {
        // Best effort — Settings → Notifications can always re-enable manually.
      }
      void settingsInvalidations.notifications.invalidate(qc);
    },
  });
}

export function useTotpEnroll() {
  return useMutation({
    mutationFn: (password: string) =>
      api('/api/auth/totp/enroll', {
        method: 'POST',
        body: { password },
        schema: TotpEnrollResponse,
      }),
  });
}

export function useTotpConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api('/api/auth/totp/confirm', { method: 'POST', body: { code }, schema: TotpConfirmResponse }),
    onSuccess: () => appInvalidations.auth.invalidate(qc),
  });
}

export function useTotpDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { password: string; code: string }) =>
      api('/api/auth/totp/disable', { method: 'POST', body }),
    onSuccess: () => appInvalidations.auth.invalidate(qc),
  });
}

// ---- users + invites (admin) ----

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: appKeys.users,
    queryFn: () => api('/api/users', { schema: UserList }),
    enabled,
  });
}

export function usePatchUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UserPatch }) =>
      api(`/api/users/${id}`, { method: 'PATCH', body }),
    onSettled: () => appInvalidations.users.invalidate(qc),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/users/${id}`, { method: 'DELETE' }),
    onSettled: () => appInvalidations.users.invalidate(qc),
  });
}

export function useInvites(enabled = true) {
  return useQuery({
    queryKey: appKeys.invites,
    queryFn: () => api('/api/invites', { schema: InviteList }),
    enabled,
  });
}

export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api('/api/invites', { method: 'POST', schema: InviteCreateResponse }),
    onSettled: () => appInvalidations.invites.invalidate(qc),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/invites/${id}`, { method: 'DELETE' }),
    onSettled: () => appInvalidations.invites.invalidate(qc),
  });
}

export function useInviteCheck(token: string) {
  return useQuery({
    queryKey: appKeys.inviteCheck(token),
    queryFn: () => api(`/api/invites/check/${token}`, { schema: InviteCheckResponse }),
    retry: false,
  });
}

export function useInviteAccept(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      api(`/api/invites/accept/${token}`, { method: 'POST', body }),
    onSuccess: () => resetIdentityBoundState(qc),
  });
}

// ---- version ----

export function useVersion() {
  return useQuery({
    queryKey: appKeys.version,
    queryFn: () => api('/api/version', { schema: VersionInfo }),
    staleTime: Infinity,
  });
}

// ---- automation API ----

export function useAutomationTokens() {
  return useQuery({
    queryKey: settingsKeys.automationTokens,
    queryFn: () => api('/api/automation/tokens', { schema: AutomationTokenList }),
  });
}

export function useAutomationActivity() {
  return useQuery({
    queryKey: settingsKeys.automationActivity,
    queryFn: () => api('/api/automation/activity', { schema: AutomationActivityList }),
    refetchInterval: 30_000,
  });
}

export function useCreateAutomationToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AutomationTokenCreate) =>
      api('/api/automation/tokens', {
        method: 'POST',
        body,
        schema: AutomationTokenCreateResponse,
      }),
    onSettled: () => settingsInvalidations.automationTokens.invalidate(qc),
  });
}

export function useRevokeAutomationToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/automation/tokens/${id}`, { method: 'DELETE' }),
    onSettled: () => {
      void settingsInvalidations.automation.invalidate(qc);
    },
  });
}

export function useRevokeAllAutomationTokens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api('/api/automation/tokens/revoke-all', {
        method: 'POST',
        schema: AutomationTokenRevokeResponse,
      }),
    onSettled: () => {
      void settingsInvalidations.automation.invalidate(qc);
    },
  });
}

// ---- mail accounts ----

export function useAccounts(enabled = true) {
  return useQuery({
    queryKey: mailKeys.accounts,
    queryFn: () => api('/api/accounts', { schema: AccountList }),
    enabled,
  });
}

export function useFolders(enabled = true) {
  return useQuery({
    queryKey: mailKeys.folders,
    queryFn: () => api('/api/folders', { schema: FolderList }),
    enabled,
  });
}

export function useMailNavigationCounts(enabled = true) {
  return useQuery({
    queryKey: mailKeys.navigationCounts,
    queryFn: () =>
      api('/api/mail/navigation-counts', { schema: MailNavigationCounts }),
    enabled,
  });
}

function useInvalidateAccounts() {
  const qc = useQueryClient();
  return () => {
    void mailInvalidations.accounts.invalidate(qc);
  };
}

export function useCreateAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (body: AccountCreate) => api<Account>('/api/accounts', { method: 'POST', body }),
    onSettled: invalidate,
  });
}

export function useUpdateAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AccountUpdate }) =>
      api<Account>(`/api/accounts/${id}`, { method: 'PATCH', body }),
    onSettled: invalidate,
  });
}

export function useDeleteAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (id: number) => api(`/api/accounts/${id}`, { method: 'DELETE' }),
    onSettled: invalidate,
  });
}

export function useTestAccount() {
  return useMutation({
    mutationFn: (body: AccountTest) =>
      api('/api/accounts/test', { method: 'POST', body, schema: AccountTestResult }),
  });
}

export function useResyncAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (id: number) => api<Account>(`/api/accounts/${id}/resync`, { method: 'POST' }),
    onSettled: invalidate,
  });
}

// ---- sync activity ----

export function useSyncStatus() {
  return useQuery({
    queryKey: mailKeys.syncStatus,
    queryFn: () => api('/api/sync/status', { schema: SyncStatusResponse }),
    refetchInterval: (query) => {
      const status = query.state.data;
      const active = status?.accounts.some(
        (account) =>
          account.state === 'syncing' ||
          account.backfillFoldersRemaining > 0 ||
          account.rebindMessagesRemaining > 0 ||
          account.missingMessageCount > 0 ||
          account.queue.pending > 0 ||
          account.queue.inflight > 0 ||
          account.outbox.sentCopyPending > 0,
      );
      return active ? 5_000 : 30_000;
    },
  });
}

export function useStorageCapacity(enabled = true) {
  return useQuery({
    queryKey: settingsKeys.storageCapacity,
    queryFn: () => api('/api/storage/capacity', { schema: StorageCapacityResponse }),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useRefreshMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId?: number) =>
      api('/api/sync/refresh', {
        method: 'POST',
        body: accountId === undefined ? {} : { accountId },
        schema: SyncRefreshResponse,
      }),
    onSettled: () => {
      void mailInvalidations.syncOverview.invalidate(qc);
    },
  });
}

export function useVerifyMailMirror() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      folderId,
    }: {
      accountId: number;
      folderId?: number;
    }) =>
      api('/api/sync/reconcile', {
        method: 'POST',
        body: folderId === undefined ? { accountId } : { accountId, folderId },
        schema: SyncReconcileResponse,
      }),
    onSettled: () => {
      void mailInvalidations.syncStatus.invalidate(qc);
    },
  });
}

export function useRetryOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/sync/operations/${id}/retry`, {
        method: 'POST',
        schema: OperationRetryResponse,
      }),
    onSettled: () => mailInvalidations.syncStatus.invalidate(qc),
  });
}

export function useAcknowledgeOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/sync/operations/${id}/acknowledge`, {
        method: 'POST',
        schema: OperationAcknowledgeResponse,
      }),
    onSettled: () => mailInvalidations.syncStatus.invalidate(qc),
  });
}

export function useAcknowledgeAllOperations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api('/api/sync/operations/acknowledge-all', {
        method: 'POST',
        schema: OperationAcknowledgeResponse,
      }),
    onSettled: () => mailInvalidations.syncStatus.invalidate(qc),
  });
}
